/**
 * Alerting the table that a vote has opened.
 *
 * The projector is the mechanism: it is the one device guaranteed awake, plugged
 * in and attached to a speaker everyone can hear. Phones are best-effort — there
 * is no wake lock and no web push (iOS requires a Home Screen install, which
 * guests will not do).
 */

let ctx: AudioContext | null = null;

/**
 * Audio needs a prior user gesture in the tab, so the context is unlocked
 * during the join flow. Without this the cue fails silently later.
 */
export function unlockAudio(): void {
  try {
    const Ctor = window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    ctx ??= new Ctor();
    void ctx.resume();
  } catch {
    /* Audio is a bonus, never a requirement. */
  }
}

/** A short two-tone call, deliberately unlike a notification chime. */
export function playVoteCue(): void {
  try {
    if (!ctx) unlockAudio();
    if (!ctx || ctx.state !== 'running') return;

    const now = ctx.currentTime;
    for (const [at, freq] of [
      [0, 392],
      [0.16, 587.33],
    ] as const) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + at);
      gain.gain.linearRampToValueAtTime(0.22, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + at + 0.28);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.3);
    }
  } catch {
    /* ignore */
  }
}

/** Android only — iOS Safari has no vibrate at all. */
export function vibrate(pattern: number | number[] = [40, 60, 40]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* ignore */
  }
}
